import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Navbar from "@/components/Navbar";
import ScoreBreakdown from "@/components/ScoreBreakdown";
import { 
  Sparkles, RefreshCw, Target, ChevronRight, Loader2,
  Droplets, AlertTriangle
} from "lucide-react";

export default function Recommendations() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [skinProfile, setSkinProfile] = useState(null);

  useEffect(() => {
    fetchRecommendations();
    fetchSkinProfile();
  }, []);

  const fetchRecommendations = async () => {
    try {
      const response = await axios.get(`${API}/recommendations?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecommendations(response.data.recommendations || []);
    } catch (error) {
      console.error("Error fetching recommendations:", error);
      toast.error("Failed to load recommendations");
    } finally {
      setLoading(false);
    }
  };

  const fetchSkinProfile = async () => {
    try {
      const response = await axios.get(`${API}/auth/skin-profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSkinProfile(response.data);
    } catch (error) {
      console.error("Error fetching skin profile:", error);
    }
  };

  const refreshRecommendations = async () => {
    setRefreshing(true);
    try {
      const response = await axios.get(`${API}/recommendations?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecommendations(response.data.recommendations || []);
      toast.success("Recommendations refreshed!");
    } catch (error) {
      toast.error("Failed to refresh recommendations");
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F7]">
        <Navbar />
        <div className="flex items-center justify-center py-32">
          <div className="animate-pulse-soft text-center">
            <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto">
              <Sparkles className="w-8 h-8 text-primary/40" />
            </div>
            <p className="mt-4 text-muted-foreground">Finding your perfect products...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="recommendations-page">
      <Navbar />
      
      <main className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-serif text-3xl md:text-4xl text-primary flex items-center gap-3">
              <Sparkles className="w-8 h-8" />
              Recommended For You
            </h1>
            <p className="text-muted-foreground mt-2">
              AI-powered product recommendations based on your skin profile
            </p>
          </div>
          <Button
            onClick={refreshRecommendations}
            disabled={refreshing}
            variant="outline"
            className="gap-2"
            data-testid="refresh-recommendations-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>

        {/* Skin Profile Summary */}
        {skinProfile && (
          <Card className="card-product rounded-3xl mb-8 bg-gradient-to-r from-primary/5 to-primary/10">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                    <Droplets className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Your Skin Type</p>
                    <p className="font-semibold text-primary capitalize">{skinProfile.skin_type || 'Not set'}</p>
                  </div>
                </div>
                
                {skinProfile.concerns && skinProfile.concerns.length > 0 && (
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground mb-2">Your Concerns</p>
                    <div className="flex flex-wrap gap-2">
                      {skinProfile.concerns.map((concern, idx) => (
                        <Badge key={idx} variant="secondary" className="bg-primary/10 text-primary">
                          <Target className="w-3 h-3 mr-1" />
                          {concern.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/settings')}
                  className="text-primary"
                >
                  Update Profile
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recommendations Grid */}
        {recommendations.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {recommendations.map((rec, idx) => (
              <Card 
                key={rec.id || idx}
                className="card-product rounded-3xl overflow-hidden hover:shadow-lg transition-all cursor-pointer group"
                onClick={() => navigate(`/products?highlight=${rec.id}`)}
                data-testid={`recommendation-card-${idx}`}
              >
                <CardContent className="p-6">
                  {/* Product Image or Placeholder */}
                  {rec.image_url ? (
                    <div className="w-full h-32 mb-4 rounded-xl overflow-hidden bg-gray-100">
                      <img 
                        src={rec.image_url} 
                        alt={rec.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    </div>
                  ) : (
                    <div className="w-full h-32 mb-4 rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center">
                      <Sparkles className="w-12 h-12 text-primary/30" />
                    </div>
                  )}

                  {/* Product Info */}
                  <div className="space-y-2">
                    {rec.brand && (
                      <p className="text-xs font-semibold text-primary/70 uppercase tracking-wide">
                        {rec.brand}
                      </p>
                    )}
                    <h3 className="font-semibold text-lg leading-tight group-hover:text-primary transition-colors">
                      {rec.name}
                    </h3>
                    
                    {/* Score */}
                    <div className="flex items-center gap-2">
                      {rec.skiniq_score && (
                        <ScoreBreakdown score={rec.skiniq_score} size="small" />
                      )}
                    </div>

                    {/* Match Reason */}
                    <p className="text-sm text-muted-foreground mt-2">
                      {rec.match_reason}
                    </p>

                    {/* Target Concerns */}
                    {rec.target_concerns && rec.target_concerns.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-3">
                        {rec.target_concerns.map((concern, cIdx) => (
                          <Badge 
                            key={cIdx} 
                            variant="outline" 
                            className="text-[10px] px-2 py-0.5 border-primary/30 text-primary/70"
                          >
                            {concern}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* View Button */}
                  <div className="mt-4 pt-4 border-t border-border/50">
                    <Button variant="ghost" size="sm" className="w-full group-hover:bg-primary/5">
                      View Product
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="card-product rounded-3xl">
            <CardContent className="p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">No Recommendations Yet</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Add more products to your library and complete your skin profile to get personalized recommendations.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={() => navigate('/add-product')}>
                  Add Products
                </Button>
                <Button variant="outline" onClick={() => navigate('/settings')}>
                  Complete Profile
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Future Features Placeholder */}
        <Card className="card-product rounded-3xl mt-8 bg-gradient-to-r from-primary/5 to-transparent border-dashed">
          <CardContent className="p-8 text-center">
            <Sparkles className="w-10 h-10 text-primary/40 mx-auto mb-3" />
            <h3 className="font-semibold text-lg text-primary/70 mb-2">More Coming Soon</h3>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              We're working on product comparisons, ingredient conflict detection, 
              and shopping recommendations from your favorite stores.
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
